import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Link from '@amzn/meridian/link';
import Responsive from '@amzn/meridian/responsive';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import DataSource from '../../api';
import { StateContext } from '../../GlobalStateStore';
import { Profile } from '../../Types';
import DataState from '../../Types/DataState';
import { weeksList, getDateRangeFromWeek } from '../../Util';
import {
  Feature,
  getAlertContentFromConfig,
  getRoleBannersFromConfig,
  isFeatureEnabled,
} from '../../config/FeaturesConfig';
import { DashboardBanner } from '../../Types/DashboardBanner';
import {
  DISPLAY,
  EmptyFilters,
  NOTDISPLAY,
} from './DashboardComponents/LighthouseMetrics/helper';
import {
  LighthouseFilters,
  TimeOption,
} from './DashboardComponents/LighthouseMetrics/types';
import CoachingContainer from './DashboardContainers/CoachingContainer';
import CommentsContainer from './DashboardContainers/CommentsContainer';
import CoachingStatsContainer from './DashboardContainers/CoachingStats/CoachingStatsContainer';
import DataContainer from './DashboardContainers/DataContainer';
import ProfileContainer from './DashboardContainers/ProfileContainer';
import ContactReviewContainer from './DashboardContainers/ContactReviewContainer';
import GoalSettingContainerWrapper from '../Goals/GoalSettingContainer';
import { ConsolidatedContact } from '../../api/CoachingNotes/schema';
import moment from 'moment';
import CreateGoal from '../Goals/CreateGoal';
import { GoalPage } from '../Goals/helper';
import {
  QueryVariables,
  QueryVariablesCustomerComments,
  QueryVariablesRange,
} from '../../api/Naws';
import CSASummariesContainer from '../CSASummaries/CSASummariesContainer';
import { isOmViewingTheirOwnProfile } from './helper';
import useIsCSASummariesEnabled from '../CSASummaries/hooks/useIsCSASummariesEnabled';

interface DashboardProps {
  loggedInProfile: Profile;
  displayedProfile: Profile;
  loggedInProfileState: string;
  displayedProfileState: string;
}

const TRAILING_DAYS = 'Trailing 7 days';

const Dashboard: React.FC<DashboardProps> = ({
  loggedInProfile,
  displayedProfile,
  loggedInProfileState,
  displayedProfileState,
}: DashboardProps): React.ReactElement => {
  const apiNaws = DataSource.getDataFeed('NAWS');
  const intl = useIntl();

  const routeParams = useParams();

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;

  const {
    userID,
    enableCesComments,
    enableCoachingNotes,
    cscEntityPrefix,
    isTm,
    isAdmin,
    isExclusivelyTm,
    isExclusivelyOm,
    isOm,
  } = inspireOptions.data;

  const enableProfanityFilter = isFeatureEnabled(Feature.PROFANITY_FILTER);
  const userId = routeParams.userId ? routeParams.userId : userID;
  const userIdWithPrefix =
    userId && cscEntityPrefix ? `${cscEntityPrefix}:${userId}` : '';
  const isProfileTm = displayedProfile.directReports.length > 0;
  const hasCoachees = isAdmin
    ? isProfileTm || displayedProfile.sharedProfileCoachees.length > 0
    : isProfileTm;

  const [contactList, setContactList] = useState<ConsolidatedContact[]>([]);
  const [contactListState, setContactListState] = useState(DataState.LOADING);

  const legacyCoachingCenter = !isFeatureEnabled(Feature.NEW_COACHING);
  const achtEnabled = isFeatureEnabled(Feature.ACHT_FLAG);
  const concessionEnabled = isFeatureEnabled(Feature.CONCESSION_RATE_METRIC);

  const dashboardNotificationEnabled = isFeatureEnabled(
    Feature.DASHBOARD_NOTIFICATIONS
  );
  const dashboardAlertEnabled = isFeatureEnabled(Feature.DASHBOARD_ALERT);
  const enableGoalSetting = isFeatureEnabled(Feature.GOAL_SETTING);
  const enableCoachingStatsV2 = isFeatureEnabled(Feature.COACHING_STATS_V2);
  const csaSummariesEnabled = useIsCSASummariesEnabled({
    managerId: loggedInProfile.managers[0]?.userID,
  });

  const isOmViewingOwnProfile = isOmViewingTheirOwnProfile(
    isOm,
    loggedInProfile.user.userID,
    displayedProfile.user.userID
  );

  const showCoachingStatsV2 =
    isProfileTm &&
    (isExclusivelyTm || !isOmViewingOwnProfile) &&
    enableCoachingStatsV2;
  const showGoalSetting = enableGoalSetting && !isOmViewingOwnProfile;
  const showCsaSummaries = csaSummariesEnabled && !isOmViewingOwnProfile;
  const showCesComments = enableCesComments && !isOmViewingOwnProfile;
  const showContactReviews = !isOmViewingOwnProfile;
  const showCoachingNotes = enableCoachingNotes && !isOmViewingOwnProfile;

  const weekOptions = [TRAILING_DAYS, ...weeksList()];
  const [weekValue, setWeekValue] = useState(weekOptions[0]);

  const [coachingNotes, setCoachingNotes] = useState([]);
  const [coachingNotesState, setCoachingNotesState] = useState(
    DataState.LOADING
  );

  const [customerComments, setCustomerComments] = useState([]);
  const [customerCommentsState, setCustomerCommentsState] = useState(
    DataState.LOADING
  );

  const [lighthouseFilters, setLighthouseFilters] =
    useState<LighthouseFilters>(EmptyFilters);
  const [lighthouseFiltersState, setLighthouseFiltersState] = useState(
    DataState.LOADING
  );
  const [LHDefaultVertical, setLHDefaultVertical] = useState('');
  const [LHDefaultVerticalState, setLHDefaultVerticalState] = useState(
    DataState.LOADING
  );

  const [syncTimeChangeCount, setSyncTimeChangeCount] = useState(0);

  const [refreshGoalsFlag, setRefreshGoalsFlag] = useState(false);
  const handleAddGoal = () => {
    setRefreshGoalsFlag(!refreshGoalsFlag);
  };
  const [isCreateGoalOpen, setIsCreateGoalOpen] = useState(false);

  const refreshContacts = useCallback(() => {
    const { userName } = displayedProfile.user;
    let startWeek, endWeek, timeOption, timestamp;
    if (weekValue === TRAILING_DAYS) {
      timeOption = TimeOption.DAILY;

      // Since the data is stored in UTC format, we prepare the query parameters in UTC timezone.
      const now = moment().utc();

      // YYYY refers to the year of the day
      timestamp = now.format('YYYY-MM-DD');

      // GGGG refers to the year of the week. This may be different from YYYY
      // When part of the week is from 2024 and part of it is from 2025. e.g. 2024-12-30 (Monday)
      // In such cases, GGGG refers to the new year (e..g 2025) and YYYY refers to the previous year (e.g. 2024).
      startWeek = moment().subtract(2, 'w').utc().format('GGGG-WW');
      endWeek = now.format('GGGG-WW');
    } else {
      timeOption = TimeOption.WEEKLY;
      timestamp = weekValue;
      startWeek = moment(weekValue, 'GGGG-WW')
        .subtract(4, 'w')
        .utc()
        .format('GGGG-WW');
      endWeek = moment(weekValue, 'GGGG-WW').utc().format('GGGG-WW');
    }

    if (userName !== '') {
      const queryVariablesNaws = apiNaws.prepareQueryforRecommendationsGetter(
        userIdWithPrefix,
        userName,
        startWeek,
        endWeek,
        timeOption,
        timestamp,
        false
      );
      apiNaws.getConsolidatedContacts(
        queryVariablesNaws,
        setContactList,
        setContactListState
      );
    }
  }, [apiNaws, displayedProfile.user, userIdWithPrefix, weekValue]);

  const queryCoachingHistory = useCallback(
    (
      queryVariablesCoachingNotes: QueryVariablesCustomerComments,
      queryVariablesCoachedNote: QueryVariables | QueryVariablesRange
    ) => {
      if (inspireOptions.state !== DataState.LOADED || !userId || !weekValue) {
        return;
      }

      queryVariablesCoachingNotes =
        queryVariablesCoachingNotes ??
        apiNaws.prepareQueryForCustomerComment({
          selectedWeek: weekValue,
          userID: userId,
          cscEntityPrefix,
          enableProfanityFilter,
        });

      const onlyDeliverd = true;
      queryVariablesCoachedNote =
        queryVariablesCoachedNote ??
        apiNaws.prepareQuery(
          weekValue,
          userId,
          cscEntityPrefix,
          null,
          onlyDeliverd
        );

      if (enableCoachingNotes) {
        if (legacyCoachingCenter) {
          apiNaws.getCoachingNotes(
            queryVariablesCoachingNotes,
            setCoachingNotes,
            setCoachingNotesState
          );
        } else {
          apiNaws.getCoachingNotes(
            queryVariablesCoachingNotes,
            setCoachingNotes,
            setCoachingNotesState
          );
          apiNaws.getCoachingHistory(
            queryVariablesCoachedNote,
            false,
            setCoachingHistory,
            setCoachingHistoryState
          );
        }
      }
    },
    [userId, weekValue]
  );

  useEffect(() => {
    if (
      displayedProfile.user.userID === '' ||
      displayedProfileState === DataState.LOADING
    ) {
      return;
    }

    // Initialize filter Options to Avoid Race Condition
    if (lighthouseFilters !== EmptyFilters) {
      setLighthouseFilters(EmptyFilters);
      setLighthouseFiltersState(DataState.LOADING);
    }
    const isProfileTm = displayedProfile.directReports.length > 0;

    const queryVariablesFilters = apiNaws.prepareLighthouseFiltersQuery(
      displayedProfile.user.userID,
      cscEntityPrefix,
      isProfileTm,
      weekValue,
      weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY,
      isProfileTm ? displayedProfile.directReports : [],
      isProfileTm ? displayedProfile.directReports.map(report => false) : []
      // syncMetrics
    );

    apiNaws.getLighthouseFilters(
      queryVariablesFilters,
      setLighthouseFilters,
      setLighthouseFiltersState
    );
  }, [
    weekValue,
    displayedProfile.user.userID,
    displayedProfileState,
    syncTimeChangeCount,
  ]);

  useEffect(() => {
    if (
      displayedProfile.user.userID === '' ||
      displayedProfileState === DataState.LOADING
    ) {
      return;
    }

    const queryVariables = apiNaws.prepareLighthouseDefaultVerticalQuery(
      displayedProfile.user.userID,
      cscEntityPrefix
    );

    apiNaws.getDefaultVertical(
      queryVariables,
      setLHDefaultVertical,
      setLHDefaultVerticalState
    );
  }, [weekValue, displayedProfile.user.userID, displayedProfileState]);

  const [coachingHistory, setCoachingHistory] = useState([]);
  const [coachingHistoryState, setCoachingHistoryState] = useState(
    legacyCoachingCenter ? DataState.LOADED : DataState.LOADING
  );

  let { startDate: contactStartDate, endDate: contactEndDate } =
    getDateRangeFromWeek(weekValue);

  useEffect(() => {
    const { startDate, endDate } = getDateRangeFromWeek(weekValue);
    contactStartDate = startDate;
    contactEndDate = endDate;
  }, [weekValue]);

  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED) {
      if (!userId) {
        return;
      }
      const queryVariablesCustomerComments =
        apiNaws.prepareQueryForCustomerComment({
          selectedWeek: weekValue,
          userID: userId,
          cscEntityPrefix,
          enableProfanityFilter,
        });

      const onlyDeliverd = true;
      const queryVariablesCoachedNote = apiNaws.prepareQuery(
        weekValue,
        userId,
        cscEntityPrefix,
        null,
        onlyDeliverd
      );

      if (weekValue) {
        if (showCesComments) {
          apiNaws.getCustomerCommentsData(
            queryVariablesCustomerComments,
            setCustomerComments,
            setCustomerCommentsState
          );
        }

        queryCoachingHistory(
          queryVariablesCustomerComments,
          queryVariablesCoachedNote
        );

        if (showContactReviews) {
          refreshContacts();
        }
      }
    }
  }, [
    weekValue,
    inspireOptions.state,
    userId,
    apiNaws,
    cscEntityPrefix,
    enableProfanityFilter,
    enableCesComments,
    queryCoachingHistory,
    refreshContacts,
  ]);

  const setWeek = (e: React.FormEvent<HTMLSelectElement>): void => {
    setWeekValue(e.toString());
  };

  return (
    <Box>
      <Row
        width='100%'
        widths={['fill', 'fit']}
        alignmentVertical='top'
        height='100%'
      >
        <Responsive
          query='min-width'
          props={{
            isBig: {
              default: false,
              '1080px': true,
            },
            hmdTruncate: {
              '512px': ['grid-12', 'grid-12', 'grid-12'],
              '860px': ['grid-6', 'grid-6', 'grid-12'],
              '1080px': ['grid-4', 'grid-4', 'grid-4'],
            },
            cardTruncate: {
              '512px': '100%',
              '628px': '50%',
              '1210px': '33.33%',
              default: 'fill',
            },
            filterTruncate: {
              '512px': '100%',
              '860px': '50%',
              '1210px': '25%',
            },
            cardWidth: {
              '512px': '448px',
              '628px': '275px',
              '745px': '332px',
              '860px': '390px',
              '985px': '448px',
              '1210px': '372px',
              '1327px': '410px',
              '1440px': '448px',
            },
            subMetricsTitleDisplay: {
              '512px': DISPLAY,
              '628px': NOTDISPLAY,
              '985px': DISPLAY,
              '1210px': NOTDISPLAY,
              '1440px': DISPLAY,
            },
            scoreHeader: {
              '512PX': DISPLAY,
              '628px': NOTDISPLAY,
              '977px': DISPLAY,
              '1210px': NOTDISPLAY,
              '1440px': DISPLAY,
            },
            scoreFooter: {
              '512px': DISPLAY,
              '628px': NOTDISPLAY,
              '860px': DISPLAY,
            },
            responsiveFont: {
              '512px': true,
              '628px': false,
              '745px': true,
            },
          }}
        >
          {({
            isBig,
            hmdTruncate,
            cardTruncate,
            filterTruncate,
            subMetricsTitleDisplay,
            cardWidth,
            scoreHeader,
            scoreFooter,
            responsiveFont,
          }: any): React.ReactElement => (
            <>
              <Box height='100%'>
                {dashboardNotificationEnabled &&
                  (() => {
                    let userRole: string;
                    if (isExclusivelyOm) {
                      userRole = 'OM';
                    } else if (isExclusivelyTm) {
                      userRole = 'TM';
                    } else if (!isTm) {
                      userRole = 'CSA'; // person is CSA if isTm is false
                    } else {
                      userRole = 'CSA'; // default fallback
                    }
                    // Try new format first, then fall back to old format
                    let roleBanners = getRoleBannersFromConfig(
                      Feature.DASHBOARD_NOTIFICATIONS,
                      userRole
                    );
                    return roleBanners.map((banner: DashboardBanner) => (
                      <Box key={banner.id} spacingInset='none none 500'>
                        <Alert>
                          <Text>{banner.content}</Text>
                          {banner.linkHref !== undefined &&
                            banner.linkText !== undefined && (
                              <Link
                                data-csa-c-type='element'
                                data-csa-c-slot-id='dashboard-coaching-history-slot'
                                data-csa-c-content-id='dashboard-coaching-history-link'
                                href={banner.linkHref}
                                target='_blank'
                                type='secondary'
                              >
                                {banner.linkText}
                              </Link>
                            )}
                        </Alert>
                      </Box>
                    ));
                  })()}

                {dashboardAlertEnabled &&
                  getAlertContentFromConfig(Feature.DASHBOARD_ALERT) !==
                    null && (
                    <Box spacingInset='none none 500'>
                      <Alert type='warning'>
                        <Text>
                          {getAlertContentFromConfig(Feature.DASHBOARD_ALERT)}
                        </Text>
                      </Alert>
                    </Box>
                  )}

                <ProfileContainer
                  state={displayedProfileState}
                  profile={displayedProfile}
                  selectedWeek={weekValue}
                  setWeek={setWeek}
                  isBig={isBig}
                />
                {showCoachingStatsV2 && (
                  <CoachingStatsContainer
                    state={displayedProfileState}
                    profile={displayedProfile}
                  />
                )}

                {showCsaSummaries && (
                  <>
                    <CSASummariesContainer
                      key={displayedProfile.user.alias}
                      displayedProfile={displayedProfile}
                      loggedInProfile={loggedInProfile}
                      profilesLoaded={
                        displayedProfileState === DataState.LOADED &&
                        loggedInProfileState === DataState.LOADED
                      }
                      isProfileTm={isProfileTm}
                    />
                    <Box spacingInset='500 none none none' />
                  </>
                )}

                <DataContainer
                  user={displayedProfile.user}
                  cscEntityPrefix={cscEntityPrefix}
                  isBig={isBig}
                  cardTruncate={cardTruncate}
                  filterTruncate={filterTruncate}
                  displaySubtitles={subMetricsTitleDisplay}
                  profile={displayedProfile}
                  isProfileTm={isProfileTm}
                  achtEnabled={achtEnabled}
                  concessionEnabled={concessionEnabled}
                  setWeek={setWeek}
                  weekValue={weekValue}
                  weekOptions={weekOptions}
                  LHDefaultVertical={LHDefaultVertical}
                  LHDefaultVerticalState={LHDefaultVerticalState}
                  lighthouseFilters={lighthouseFilters}
                  lighthouseFiltersState={lighthouseFiltersState}
                  cardWidth={cardWidth}
                  displayScoreHeader={scoreHeader}
                  displayScoreFooter={scoreFooter}
                  responsiveFont={responsiveFont}
                  setSyncTimeChangeCount={setSyncTimeChangeCount}
                />
                {showGoalSetting && (
                  <GoalSettingContainerWrapper
                    loading={
                      displayedProfileState === DataState.LOADING ||
                      loggedInProfileState === DataState.LOADING
                    }
                    loaded={
                      displayedProfileState === DataState.LOADED &&
                      loggedInProfileState === DataState.LOADED
                    }
                    error={
                      displayedProfileState === DataState.ERROR ||
                      loggedInProfileState === DataState.ERROR
                    }
                    displayedProfile={displayedProfile}
                    loggedInProfile={loggedInProfile}
                    refreshGoalsFlag={refreshGoalsFlag}
                    setIsCreateGoalOpen={setIsCreateGoalOpen}
                    page={GoalPage.DASHBOARD}
                  />
                )}

                {showCesComments && (
                  <CommentsContainer
                    state={customerCommentsState}
                    customerComments={customerComments}
                    contactStartDate={contactStartDate}
                    contactEndDate={contactEndDate}
                    contactHandlerId={userIdWithPrefix}
                  />
                )}

                {showContactReviews && (
                  <ContactReviewContainer
                    profile={displayedProfile}
                    state={contactListState}
                    contacts={contactList}
                    userID={userIdWithPrefix}
                    sourcePage={'Dashboard'}
                    refreshContacts={refreshContacts}
                  />
                )}

                {showCoachingNotes && (
                  <CoachingContainer
                    coachingNotesState={coachingNotesState}
                    coachingHistoryState={coachingHistoryState}
                    coachingHistory={coachingHistory}
                    coachingNotes={coachingNotes}
                    profile={displayedProfile}
                    profileState={displayedProfileState}
                    refreshCoachingHistory={queryCoachingHistory}
                  />
                )}
              </Box>
              {showGoalSetting && (
                <CreateGoal
                  isOpen={isCreateGoalOpen}
                  setIsOpen={setIsCreateGoalOpen}
                  lockedGoalLevel={!hasCoachees}
                  defaultAssociateID={
                    !hasCoachees ? displayedProfile.user.userID : undefined
                  }
                  cscEntityPrefix={inspireOptions.data.cscEntityPrefix}
                  profile={loggedInProfile}
                  changeFlag={handleAddGoal}
                  page={GoalPage.DASHBOARD}
                />
              )}
            </>
          )}
        </Responsive>
      </Row>
    </Box>
  );
};

export default Dashboard;
